import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AutomotiveBusinessLevel } from "@enums/automotiveBusinessLevel.enum";
import { PpapCapability } from "@enums/ppapCapability.enum";
import { YesNoConsidering } from "@enums/yesNoConsidering.enum";
import { YesNoNa } from "@enums/yesNoNa.enum";
import { SupplierQuestionnaire } from "@interfaces/models/supplierQuestionnaire.interface";
import { SupplierQuestionnaireContactLineData } from "@interfaces/supplierQuestionnaireContactLineData.interface";
import { FileService } from "@services/file.service";
import { SupplierQuestionnaireService } from "@services/supplierQuestionnaire.service";
import { ToastService } from "angular-toastify";

/**
 * Public (logged out) Approved Supplier Questionnaire — a digital Doc 31 rev 5. Reached from an emailed
 * link; the lookupId in the route is the credential, so an invalid/used link shows a friendly message.
 */
@Component({
	moduleId: module.id,
	selector: "supplierQuestionnaire",
	styleUrls: ["supplierQuestionnaire.css"],
	templateUrl: "supplierQuestionnaire.html"
})
export class SupplierQuestionnairePublicComponent {
	// The four fixed contact columns from Doc 31
	static readonly CONTACT_ROLES = ["Sales", "Quality", "Accounts", "Director / Senior Mgt (Escalation)"];

	automotiveBusinessLevels = [
		{ id: AutomotiveBusinessLevel.ZeroToFifty, name: "0-50%" },
		{ id: AutomotiveBusinessLevel.FiftyToSeventyFive, name: "50-75%" },
		{ id: AutomotiveBusinessLevel.SeventyFiveToOneHundred, name: "75-100%" },
	];
	contacts: SupplierQuestionnaireContactLineData[] = [];
	errorMessage = "";
	item: SupplierQuestionnaire = new SupplierQuestionnaire();
	loading = true;
	lookupId = "";
	ppapOptions = [
		{ id: PpapCapability.Yes, name: "Yes" },
		{ id: PpapCapability.No, name: "No" },
		{ id: PpapCapability.RequireHelp, name: "Require Help" },
	];
	saving = false;
	submitted = false;
	// Served straight from the web app's assets; a revision is a drop-in replacement of the file. Keep the
	// version in step with SupplierQuestionnaire.CurrentTermsVersion in the API, which records what was agreed.
	termsUrl = "assets/docs/supplier-terms-and-conditions.pdf";
	termsVersion = "Doc Ref 88, Issue 01 (07.01.2026)";
	yesNo = [
		{ id: true, name: "Yes" },
		{ id: false, name: "No" },
	];
	yesNoConsidering = [
		{ id: YesNoConsidering.Yes, name: "Yes" },
		{ id: YesNoConsidering.No, name: "No" },
		{ id: YesNoConsidering.Considering, name: "Considering" },
	];
	yesNoNa = [
		{ id: YesNoNa.Yes, name: "Yes" },
		{ id: YesNoNa.No, name: "No" },
		{ id: YesNoNa.NotApplicable, name: "N/A" },
	];

	constructor(private route: ActivatedRoute, private toastService: ToastService,
		private fileService: FileService, private questionnaireService: SupplierQuestionnaireService) {
		this.uploadCertificate = this.uploadCertificate.bind(this);

		this.route.params.subscribe((params) => {
			this.lookupId = params.lookupId;
			this.getQuestionnaire();
		});
	}

	private buildContacts() {
		let existing: SupplierQuestionnaireContactLineData[] = [];
		try {
			existing = JSON.parse(this.item.contactsLineData || "[]");
		} catch (e) {
			existing = [];
		}

		// Always render the four fixed Doc 31 columns, keeping anything already captured.
		this.contacts = SupplierQuestionnairePublicComponent.CONTACT_ROLES.map((role) => {
			const match = existing.find((c) => c.role === role);
			return match ? new SupplierQuestionnaireContactLineData(match) : new SupplierQuestionnaireContactLineData({ role });
		});
	}

	getQuestionnaire() {
		this.loading = true;
		this.questionnaireService.public_GetByLookup(this.lookupId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.buildContacts();
					this.loading = false;
				},
				(err) => {
					console.log(err);
					this.loading = false;
					this.errorMessage = "This questionnaire link is not valid, or has already been completed. Please contact BPL Engineering Group if you believe this is a mistake.";
				}
			);
	}

	/**
	 * Comparison target for the "accept the terms" tick box — the value must equal true to validate.
	 */
	mustBeAccepted() {
		return true;
	}

	submit() {
		if (this.saving) return;

		if (!this.item.completedByName || !this.item.signature) {
			this.toastService.error("Please complete the 'Completed By' name and signature before submitting.");
			return;
		}

		if (!this.item.termsAccepted) {
			this.toastService.error("Please accept the Terms and Conditions before submitting.");
			return;
		}

		this.saving = true;
		this.item.contactsLineData = JSON.stringify(this.contacts);
		if (!this.item.completedOn) { this.item.completedOn = new Date(); }

		this.questionnaireService.public_Submit(this.lookupId, this.item)
			.subscribe(
				(res: any) => {
					this.saving = false;
					this.submitted = true;
					// Show what the server actually recorded — the submission time and fingerprint are
					// stamped there, not here, so the confirmation reflects the real audit trail.
					if (res && res.response) { this.item = res.response; }
				},
				(err) => {
					this.saving = false;
					console.log(err);
					this.toastService.error("Could not submit the questionnaire. Please try again.");
				}
			);
	}

	/**
	 * Certificate uploads use the anonymous App_Upload endpoint; the returned file id is stored against
	 * the matching standard on the questionnaire and filed against the supplier when we submit.
	 */
	uploadCertificate(field: string, file: any) {
		const uploadData = new FormData();
		uploadData.append("files", file);

		this.fileService.app_upload(uploadData)
			.subscribe(
				(res: any) => {
					(this.item as any)[field] = res.response[0].id;
					this.toastService.success("File uploaded.");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Could not upload that file.");
				}
			);
	}

}
